import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

type JsonRecord = Record<string, unknown>;

function isPlainObject(value: unknown): value is JsonRecord {
  return (
    typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof FormData) && !(value instanceof Date)
  );
}

@Injectable()
export class DateInterceptor implements HttpInterceptor {
  private readonly dateFieldRegex = /[sS]tartTs$|[eE]ndTs$/;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.body !== null && request.body !== undefined) {
      const transformedBody = this.transformDatesToStrings(request.body);
      request = request.clone({ body: transformedBody });
    }
    return next.handle(request);
  }

  private isDateField(key: string): boolean {
    return this.dateFieldRegex.test(key);
  }

  private transformDatesToStrings(body: unknown): unknown {
    if (body === null || body === undefined) return body;
    if (Array.isArray(body)) return body.map(item => this.transformDatesToStrings(item));
    if (!isPlainObject(body)) return body;

    const result: JsonRecord = { ...body };
    for (const key of Object.keys(result)) {
      const value = result[key];
      if (this.isDateField(key)) {
        if (value instanceof Date) {
          result[key] = this.formatDateForBackend(value);
        } else if (typeof value === 'string' && value.includes('T')) {
          result[key] = this.convertIsoToFlaskFormat(value);
        }
      } else if (typeof value === 'object' && value !== null) {
        result[key] = this.transformDatesToStrings(value);
      }
    }

    return result;
  }

  private convertIsoToFlaskFormat(isoDateStr: string): string {
    if (!isoDateStr) return isoDateStr;

    if (isoDateStr.endsWith('Z')) {
      const [head, tail] = isoDateStr.split('.');
      if (head !== undefined && tail !== undefined) {
        const ms = tail.replace('Z', '').padEnd(3, '0');
        return `${head}.${ms}000`;
      }
      return `${isoDateStr.slice(0, -1)}.000000`;
    }

    // Offset-qualified strings (e.g. "+00:00") parse natively server-side;
    // appending padding after the offset would corrupt them.
    if (/[+-]\d{2}:\d{2}$/.test(isoDateStr)) {
      return isoDateStr;
    }

    return isoDateStr.includes('.') ? `${isoDateStr}000` : `${isoDateStr}.000000`;
  }

  private formatDateForBackend(date: Date): string | null {
    if (!(date instanceof Date) || isNaN(date.getTime())) return null;

    const isoString = date.toISOString();
    const [head, tail] = isoString.split('.');
    if (head === undefined || tail === undefined) return isoString;
    const ms = tail.slice(0, 3);
    return `${head}.${ms}000`;
  }
}
